import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Eye, EyeOff, FileText, Target, Shield, Compass, CheckCircle, Calendar, X, Linkedin } from 'lucide-react';
import AdminPreviewBar from '../components/AdminPreviewBar';
import InstallAppSection from '../components/InstallAppSection';
import { useAuth } from '../context/AuthContext';
import '../styles/ComingSoon.css';
import '../styles/Landing.css';
import StatsReel from '../components/StatsReel';

// Cinematic image-based reel (founder spec 2026-02): 8 hand-designed slides
// + closing CTA with logo backdrop.  Single source of truth shared with /home.
const COMING_SOON_SOUND_FAMILIAR = {
  image: '/stats-reel/slide-0.png',
};

const COMING_SOON_STATS = [
  {
    id: 'cs-slide-1',
    image: '/stats-reel/slide-1.png',
    alt: 'Slide 1',
  },
  {
    id: 'cs-slide-2',
    image: '/stats-reel/slide-2.png',
    alt: 'Slide 2',
  },
  {
    id: 'cs-slide-3',
    image: '/stats-reel/slide-3.png',
    alt: 'Slide 3',
  },
  {
    id: 'cs-slide-4',
    image: '/stats-reel/slide-4.png',
    alt: 'Slide 4',
  },
  {
    id: 'cs-slide-5',
    image: '/stats-reel/slide-5.png',
    alt: 'Slide 5',
  },
  {
    id: 'cs-slide-6',
    image: '/stats-reel/slide-6.png',
    alt: 'Slide 6',
    dwellMs: 4500,
  },
  {
    id: 'cs-slide-7',
    image: '/stats-reel/slide-7.png',
    alt: 'Slide 7',
    dwellMs: 4500,
  },
];

// Closing CTA slide — Option D (Hybrid · Transparent) selected by founder
// (Feb 07 2026). C monogram + puzzle promise + tagline, single PNG floats
// on parchment with the bright-gold "Ready to Navigate" pill below.
const COMING_SOON_FILLS_GAP_CTAS = [
  {
    demoKey: 'closing',
    image: '/stats-reel/closing-option-d.png',
    transparent: true,
    goldButton: true,
    button: { label: 'Join the Waitlist', href: '#cs-waitlist' },
  },
];

const STORAGE_KEY = 'cadence_site_access';

// Image Modal with Quote
const ImageModal = ({ src, alt, video, onClose }) => (
  <div className="cs-image-modal-overlay" onClick={onClose}>
    <div className="cs-image-modal-content" onClick={(e) => e.stopPropagation()}>
      <button className="cs-image-modal-close" onClick={onClose} data-testid="cs-founders-modal-close">
        <X size={24} />
      </button>
      {video ? (
        <video
          src={video}
          controls
          autoPlay
          playsInline
          preload="metadata"
          className="cs-founders-modal-video"
          aria-label={alt}
          data-testid="cs-founders-modal-video"
        >
          Your browser does not support embedded video.
        </video>
      ) : (
        <>
          <img src={src} alt={alt} />
          <div className="cs-founders-modal-quote">
            <p className="cs-modal-quote-text">&ldquo;We created CADENCE because we refused to accept a&nbsp;healthcare system that leaves people behind&mdash;we&rsquo;re fighting for&nbsp;you.&rdquo;</p>
            <p className="cs-modal-quote-attribution">— Andera and&nbsp;Lakia</p>
          </div>
        </>
      )}
    </div>
  </div>
);

const ComingSoon = () => {
  const [expandedImage, setExpandedImage] = useState(null);
  const [expandedFeatures, setExpandedFeatures] = useState(() => new Set());
  const [sheeningIndex, setSheeningIndex] = useState(null);
  const [showGateInput, setShowGateInput] = useState(false);
  const [gateCode, setGateCode] = useState('');
  const [gateError, setGateError] = useState('');
  const [showGateCode, setShowGateCode] = useState(false);
  // Early-access "lifeline" — visitors without a code can leave their
  // email to receive one. Captured to /api/waitlist/early-access-request.
  const [showAccessRequest, setShowAccessRequest] = useState(false);
  const [accessRequestEmail, setAccessRequestEmail] = useState('');
  const [accessRequestStatus, setAccessRequestStatus] = useState('idle'); // idle | sending | sent | error
  const [accessRequestMsg, setAccessRequestMsg] = useState('');
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();

  // Early-access gate — discreet password entry tucked into the footer.
  // Soft pre-launch gate (already trivially bypassable via localStorage),
  // validated server-side so unique per-email codes (issued via the
  // "Don't have a code?" lifeline below) work alongside the universal
  // team code.
  const handleGateSubmit = async (e) => {
    e.preventDefault();
    const submitted = gateCode.trim();
    if (!submitted) {
      setGateError('Enter a code');
      setTimeout(() => setGateError(''), 2500);
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/waitlist/validate-access-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: submitted }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.valid) {
        localStorage.setItem(STORAGE_KEY, 'granted');
        window.location.href = '/home';
      } else {
        setGateError('Invalid code');
        setTimeout(() => setGateError(''), 2500);
      }
    } catch (err) {
      // Fall back to the master code client-side so the team can still
      // enter even if the API is briefly unreachable.
      if (submitted === 'Cadence2025!') {
        localStorage.setItem(STORAGE_KEY, 'granted');
        window.location.href = '/home';
        return;
      }
      setGateError('Network error');
      setTimeout(() => setGateError(''), 2500);
    }
  };

  const handleAccessRequestSubmit = async (e) => {
    e.preventDefault();
    if (accessRequestStatus === 'sending') return;
    setAccessRequestStatus('sending');
    setAccessRequestMsg('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/waitlist/early-access-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: accessRequestEmail.trim(), source: 'coming-soon-gate' }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const msg = data?.detail && Array.isArray(data.detail)
          ? 'Please enter a valid email.'
          : (data?.detail || 'Something went wrong. Please try again.');
        setAccessRequestStatus('error');
        setAccessRequestMsg(typeof msg === 'string' ? msg : 'Please enter a valid email.');
        return;
      }
      const data = await res.json();
      setAccessRequestStatus('sent');
      setAccessRequestMsg(data?.message || "Thank you — we'll be in touch.");
      setAccessRequestEmail('');
    } catch (err) {
      setAccessRequestStatus('error');
      setAccessRequestMsg('Network error. Please try again.');
    }
  };

  // Reflect the actual URL the visitor (or crawler) hit so each gated route
  // ships its own canonical instead of all aliasing to /coming-soon.
  const canonicalPath = location.pathname === '/' ? '/' : location.pathname;
  const canonicalUrl = `https://www.cadencemedicalconsulting.com${canonicalPath}`;

  // Check if admin bar will be shown
  const isAdmin = isAuthenticated && user && ['admin', 'staff', 'reviewer', 'provider'].includes(user.role);

  // Multi-expand: visitors can have ALL tiles open at once.
  const toggleFeature = (index) => {
    setExpandedFeatures((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  // Diagonal gold sheen sweep — a band of bright gold sweeps across the
  // tile like a Rolex face catching sunlight. Replaces the earlier gold-dot
  // press effect per user 2026-05-10. Driven by `sheeningIndex` state so
  // it survives the re-render triggered by setExpandedFeature.
  const handleFeatureTap = (e, index) => {
    setSheeningIndex(index);
    setTimeout(() => {
      setSheeningIndex((current) => (current === index ? null : current));
    }, 2900);
    toggleFeature(index);
  };

  // ComingSoon is rendered both as the /coming-soon route AND as the gate
  // for non-launched routes (via App.js). Only emit Helmet meta tags when
  // the visitor is actually on / or /coming-soon so we don't fight other
  // pages' meta tags inside the curtain mount tree.
  const isComingSoonRoute = location.pathname === '/' || location.pathname === '/coming-soon';

  return (
    <div className={`coming-soon-page ${isAdmin ? 'has-admin-bar' : ''}`}>
      {/* SEO Meta Tags */}
      {isComingSoonRoute && (
      <Helmet>
        <title>CADENCE Medical Consulting | Medical Expert Opinion &amp; Medical Record Review</title>
        <meta name="description" content="Medical expert opinion & comprehensive record review for those dismissed by doctors. Veteran-owned, woman-led patient advocacy. Free health education guides. Launching soon!" />
        <meta name="keywords" content="medical record review, medical expert opinion, patient advocacy, medical gaslighting, healthcare advocacy, misdiagnosis help, veteran-owned healthcare, women's health advocate" />
        {/* Prelaunch: Coming Soon is one of the two indexable routes. */}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta name="googlebot" content="index, follow" />
        <meta name="bingbot" content="index, follow" />
        <meta property="og:title" content="CADENCE Medical Consulting | Medical Expert Opinion & Record Review" />
        <meta property="og:description" content="Expert record review • Patient advocacy • Free health guides. Finally, healthcare that listens. Veteran-owned. Launching soon!" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "MedicalBusiness",
            "@id": "https://www.cadencemedicalconsulting.com/#organization",
            "name": "CADENCE Medical Consulting, LLC",
            "alternateName": ["CADENCE Medical Consulting", "CADENCE Medical"],
            "description": "Medical expert opinion and comprehensive medical record review for complex diagnoses. Veteran-owned, woman-led patient advocacy. We review YOUR records and help you get answers.",
            "url": "https://www.cadencemedicalconsulting.com",
            "logo": "https://www.cadencemedicalconsulting.com/cadence-gold-plate.png",
            "image": "https://www.cadencemedicalconsulting.com/cadence-gold-plate.png",
            "email": "info@cadencemedicalconsulting.com",
            "priceRange": "$$",
            "address": {
              "@type": "PostalAddress",
              "addressRegion": "DE",
              "addressCountry": "US"
            },
            "areaServed": { "@type": "Country", "name": "United States" },
            "medicalSpecialty": ["Family Medicine", "Women's Health", "Autoimmune Conditions", "Thyroid Disorders"],
            "founder": [
              {
                "@type": "Person",
                "name": "Lakia S. Stewart",
                "honorificSuffix": "DNP, FNP-C, WHNP-BC",
                "jobTitle": "Chief Medical Officer & Co-Founder",
                "description": "DNP-prepared nurse practitioner with 19+ years of military medical service. Board-certified FNP and WHNP."
              },
              {
                "@type": "Person",
                "name": "Andera N. Peeples",
                "honorificSuffix": "MSDP, MSML-HA",
                "jobTitle": "Chief Executive Officer & Co-Founder",
                "description": "Healthcare systems architect with 14 years Army service. Dual master's in Disaster Preparedness and Healthcare Administration."
              }
            ]
          }
        `}</script>
      </Helmet>
      )}
      
      {expandedImage && (
        <ImageModal 
          src={expandedImage.src} 
          alt={expandedImage.alt} 
          video={expandedImage.video}
          onClose={() => setExpandedImage(null)}
        />
      )}
      
      <div 
        className="silk-background"
        style={{ backgroundImage: `url('/velvet-gold.png')` }}
      ></div>
      
      {/* Admin Preview Bar - Only visible to logged-in admins */}
      <AdminPreviewBar />
      
      <div className="coming-soon-content">
        
        {/* ========== 1. HERO ========== */}
        <section className="cs-hero">
          <p className="coming-soon-label">Full Site Launching Soon...</p>

          {/* ========== EARLY-ACCESS GATE — relocated 2026-02-08
              (founder spec "I can't see the access code link, move
              it higher — below the Coming Soon label"). Lives in
              the hero just under the launching label so it's
              immediately discoverable for partners + invited beta
              users. Toggle-to-form keeps the public hero clean. */}
          <div className="cs-gate-block cs-gate-block--hero" data-testid="cs-gate-block-hero">
            {!showGateInput ? (
              <button
                type="button"
                className="cs-gate-toggle cs-gate-toggle--hero"
                onClick={() => setShowGateInput(true)}
                data-testid="cs-gate-toggle"
              >
                Have an early-access code? <ArrowRight size={14} aria-hidden="true" />
              </button>
            ) : (
              <form className="cs-gate-form" onSubmit={handleGateSubmit} data-testid="cs-gate-form">
                <div className="cs-gate-input-wrap">
                  <input
                    type={showGateCode ? 'text' : 'password'}
                    value={gateCode}
                    onChange={(e) => setGateCode(e.target.value)}
                    placeholder="Enter access code"
                    className="cs-gate-input"
                    autoFocus
                    data-testid="cs-gate-input"
                  />
                  <button
                    type="button"
                    className="cs-gate-eye"
                    onClick={() => setShowGateCode((v) => !v)}
                    aria-label={showGateCode ? 'Hide access code' : 'Show access code'}
                    aria-pressed={showGateCode}
                    data-testid="cs-gate-eye-toggle"
                    tabIndex={-1}
                  >
                    {showGateCode ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
                  </button>
                </div>
                <button type="submit" className="cs-gate-submit" data-testid="cs-gate-submit">
                  Enter
                </button>
                <button
                  type="button"
                  className="cs-gate-cancel"
                  onClick={() => { setShowGateInput(false); setGateCode(''); setGateError(''); }}
                  data-testid="cs-gate-cancel"
                  aria-label="Cancel"
                >
                  ×
                </button>
                {gateError && <p className="cs-gate-error" data-testid="cs-gate-error">{gateError}</p>}
              </form>
            )}

            {/* "Lifeline" — visitors without a code can request one.
                Lives below the gate block so it's discoverable but
                stays quiet until clicked. */}
            {!showAccessRequest ? (
              <button
                type="button"
                className="cs-gate-request-toggle"
                onClick={() => { setShowAccessRequest(true); setAccessRequestStatus('idle'); setAccessRequestMsg(''); }}
                data-testid="cs-gate-request-toggle"
              >
                Don&rsquo;t have a code? Request one <ArrowRight size={12} aria-hidden="true" />
              </button>
            ) : (
              <form
                className="cs-gate-form cs-gate-request-form"
                onSubmit={handleAccessRequestSubmit}
                data-testid="cs-gate-request-form"
              >
                <input
                  type="email"
                  value={accessRequestEmail}
                  onChange={(e) => setAccessRequestEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="cs-gate-input"
                  required
                  autoFocus
                  data-testid="cs-gate-request-email"
                  disabled={accessRequestStatus === 'sent'}
                />
                {accessRequestStatus !== 'sent' && (
                  <button
                    type="submit"
                    className="cs-gate-submit"
                    data-testid="cs-gate-request-submit"
                    disabled={accessRequestStatus === 'sending'}
                  >
                    {accessRequestStatus === 'sending' ? '…' : 'Send'}
                  </button>
                )}
                <button
                  type="button"
                  className="cs-gate-cancel"
                  onClick={() => {
                    setShowAccessRequest(false);
                    setAccessRequestEmail('');
                    setAccessRequestStatus('idle');
                    setAccessRequestMsg('');
                  }}
                  data-testid="cs-gate-request-cancel"
                  aria-label="Cancel"
                >
                  ×
                </button>
                {accessRequestMsg && (
                  <p
                    className={`cs-gate-request-msg ${accessRequestStatus === 'sent' ? 'is-success' : accessRequestStatus === 'error' ? 'is-error' : ''}`}
                    data-testid="cs-gate-request-msg"
                  >
                    {accessRequestMsg}
                  </p>
                )}
              </form>
            )}
          </div>

          {/* FOIL C - NOW AT TOP, LARGE */}
          <div className="hero-foil">
            <img 
              src="/cadence-gold-foil.png" 
              alt="CADENCE Medical Consulting" 
              className="hero-foil-img"
            />
          </div>

          <h1 className="coming-soon-headline">
            Finally, Healthcare That Listens
          </h1>

          <p className="coming-soon-subheadline">
            Expert medical record review and patient advocacy for those who&rsquo;ve been 
            dismissed, overlooked, or left without&nbsp;answers.
          </p>
        </section>

        {/* ========== 2. THE PROBLEM — 5W's plaque chassis as the
            marquee header for the stats reel below. Founder spec
            2026-02-08: "use the saved 5W's plaque grouping/styling." */}
        <section className="cs-problem-section">
          <div className="problem-plaque-wrap">
            <div className="story-fivew-plaque problem-plaque" aria-label="The Problem We're Solving">
              <div className="story-fivew-plaque-inner">
                <h2 className="story-fivew-title problem-plaque-title">The Problem We&rsquo;re Solving</h2>
                <span className="problem-plaque-subline">Rushed. Dismissed. Unheard. Unresolved.</span>
              </div>
            </div>
          </div>

          <div className="urgency-stats-section">
            <div className="container">
              <StatsReel
                soundFamiliar={COMING_SOON_SOUND_FAMILIAR}
                stats={COMING_SOON_STATS}
                ctas={COMING_SOON_FILLS_GAP_CTAS}
                testIdPrefix="cs-stats-cascade"
              />
            </div>
          </div>
        </section>

        {/* ========== 2b. SEE HOW IT WORKS — relocated 2026-02-08
            to section 4 (in place of Our Approach), per founder
            spec "How It Works belongs beneath the founder photo".
            ========== */}

        {/* ========== 2c. CADENCE ACRONYM RIBBON — uses the EXACT
            same chassis as the bottom-of-page acronym (founder spec
            2026-02-08 "the acronym needs to be exactly like the one
            at the bottom"). Two instances of the same `.acronym-tile`
            chassis is intentional — top above the founders photo,
            bottom near the footer. ========== */}
        <div className="acronym-tile" data-testid="cs-acronym-ribbon">
          <p className="acronym-footer acronym-desktop">
            CLINICAL ADVOCACY • DECISIVE EMPOWERMENT • NAVIGATING CARE & EXPERTISE
          </p>
          <p className="acronym-footer acronym-mobile">
            <span className="acronym-line">CLINICAL ADVOCACY</span>
            <span className="acronym-line">DECISIVE EMPOWERMENT</span>
            <span className="acronym-line">NAVIGATING CARE & EXPERTISE</span>
          </p>
        </div>

        {/* ========== 3. FOUNDERS ========== */}
        <section className="cs-founders-section">
          <div className="cs-founders-caption">
            <p>CADENCE&rsquo;S founders</p>
          </div>
          <div 
            className="cs-founders-photo cs-founders-clickable"
            onClick={() => setExpandedImage({ 
              video: '/local-images/founders-quote-video.mp4',
              alt: 'CADENCE Founders - Andera and Lakia quote video',
            })}
            data-testid="founders-photo-tap"
          >
            <img 
              src="/founders-office-cadence.png" 
              alt="CADENCE Founders - Andera and Lakia" 
              className="founders-img"
            />
            <span className="cs-tap-hint">Tap to hear from us</span>
          </div>
          <div className="cs-founders-experience">
            <span>36+ years combined military medical&nbsp;experience</span>
          </div>
          <Link
            to="/our-story"
            className="cs-our-story-link"
            data-testid="cs-our-story-link"
          >
            <span>Read Our Story</span>
            <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </section>

        {/* ========== 3b. UNIVERSAL TRUTH - On velvet ========== */}
        <section className="cs-truth-section" data-testid="universal-truth">
          <p className="cs-truth-text">
            Everyone has felt unheard at some point in their healthcare journey&nbsp;&mdash; 
            and everyone deserves one person who will actually listen 
            to the whole&nbsp;story.
          </p>
        </section>

        {/* ========== 4. SEE HOW IT WORKS — moved here 2026-02-08
            in place of "Our Approach" board (founder spec: "Our
            Approach board removed for now, see /app/memory/
            SAVED_OUR_APPROACH.md for future paste-back"). Frames the
            walk-through invitation directly beneath the founders +
            universal-truth so the visitor goes:
              problem → founders' truth → step inside the experience. */}
        <section className="cs-howitworks-section" data-testid="cs-howitworks-section">
          <Link
            to="/demo"
            state={{ from: '/coming-soon', fromLabel: 'Home' }}
            className="story-fivew-plaque problem-plaque cs-howitworks-plaque"
            data-testid="cs-howitworks-btn"
            aria-label="See how CADENCE works — begin the tour"
          >
            <div className="story-fivew-plaque-inner">
              <span className="story-fivew-eyebrow">Step Inside</span>
              <h2 className="story-fivew-title problem-plaque-title">See How It Works</h2>
              {/* Subline rewritten 2026-02-08 to kill the orphan word
                  ("experience." was sitting alone on its own line). */}
              <span className="problem-plaque-subline">
                A four-stage walk-through of the&nbsp;CADENCE&nbsp;experience.
              </span>
              <span className="story-fivew-plaque-hint">Begin the Tour &rarr;</span>
            </div>
          </Link>
        </section>

        {/* ========== 5. LAUNCHING BANNER — removed 2026-02-08
            (founder spec: "remove the Full Site Launching Soon
            tile/pill"; the headline label at the top of the hero
            already carries the same message). ========== */}

        {/* ========== 6. HEALTH ASSESSMENT ========== */}
        <section className="cs-offer-section">
          <div className="cs-offer-card">
            <div className="cs-offer-badge">Free For Early Supporters</div>
            <h2>Start Your Health Journey</h2>
            <p>
              Complete our confidential health assessment and unlock full access to 
              pricing, membership options, and a <strong>free 15-minute discovery&nbsp;call</strong>.
            </p>
            
            <div className="cs-offer-benefits">
              <div className="cs-offer-benefit">
                <CheckCircle size={20} />
                <span>Personalized health profile review</span>
              </div>
              <div className="cs-offer-benefit">
                <CheckCircle size={20} />
                <span>See all pricing & membership options</span>
              </div>
              <div className="cs-offer-benefit">
                <CheckCircle size={20} />
                <span>No obligation consultation</span>
              </div>
            </div>

            <Link 
              to="/portal?tab=intake&guest=true"
              className="cs-offer-btn"
              data-testid="health-assessment-btn"
            >
              <Calendar size={20} />
              Start Health Assessment
              <ArrowRight size={20} />
            </Link>
            
            <p className="cs-offer-note">Takes about 8-10 minutes • HIPAA protected</p>
            <Link to="/hipaa" className="cs-hipaa-link" data-testid="hipaa-link">
              View our HIPAA Compliance Policy
            </Link>
          </div>
        </section>

        {/* ========== 7. REQUEST CONSULTATION ========== */}
        <section className="cs-final-cta-section">
          <h2>Ready to Take the Next Step?</h2>
          <p>
            Have questions? Want to discuss your specific&nbsp;situation? 
            We&rsquo;re here to&nbsp;help.
          </p>
          <Link 
            to="/contact"
            className="cs-final-cta-btn"
            data-testid="schedule-consultation-btn"
          >
            Request a Consultation
            <ArrowRight size={20} />
          </Link>
        </section>

        {/* ========== 7b. SEE HOW IT WORKS — relocated to section
            2b (immediately under THE PROBLEM plaque) 2026-02-08. ========== */}

        {/* ========== 8. COMPASS ========== */}
        <section className="cs-compass-section">
          <div className="cs-compass-card">
            <h2>Explore<br/>CADENCE Compass</h2>
            <Link to="/compass-preview" className="compass-icon-link">
              <div className="compass-icon">
                <img src="/compass-icon.png" alt="Compass" className="compass-icon-img" />
              </div>
            </Link>
            <p>
              Free health guides, articles, and resources to help you 
              understand your health&nbsp;journey.
            </p>
            <Link 
              to="/compass-preview"
              className="cs-compass-btn"
              data-testid="compass-preview-btn"
            >
              Browse Free Resources
              <ArrowRight size={20} />
            </Link>
          </div>
        </section>

        {/* ========== 8b. INSTALL APP CTA ========== */}
        <InstallAppSection />

        {/* ========== 9. CREST - NOW AT BOTTOM, SMALLER ========== */}
        <div className="bottom-crest">
          <img 
            src="/cadence-crest.png" 
            alt="CADENCE" 
            className="bottom-crest-img"
          />
        </div>

        {/* ========== 10. ACRONYM removed 2026-02-08 — was duplicating
            the acronym already integrated into the foil C crest above
            (founder spec: "keep the foil C at the bottom, eliminate
            the redundant acronym; the higher one is the single
            instance"). ========== */}

        {/* ========== 10b. LINKEDIN (social proof at bottom) ========== */}
        <div className="cs-linkedin-block" data-testid="cs-linkedin-block">
          <a
            href="https://www.linkedin.com/company/cadencemedicalconsulting/"
            target="_blank"
            rel="noopener noreferrer"
            className="cs-linkedin-link"
            aria-label="CADENCE Medical Consulting on LinkedIn"
            data-testid="cs-linkedin-link"
          >
            <span className="cs-linkedin-icon" aria-hidden="true">
              <Linkedin size={20} strokeWidth={2.2} />
            </span>
            <span className="cs-linkedin-handle">
              linkedin.com/company/cadencemedicalconsulting
            </span>
          </a>
        </div>

        {/* Engraved museum-card sign-off — same chassis as the
            global Footer credit (2026-02-08 founder spec "add the
            engraved acronym credit below the LinkedIn lockup"). */}
        <p className="cs-acronym-credit" aria-label="CADENCE acronym sign-off">
          Clinical Advocacy &middot; Decisive Empowerment &middot; Navigating Care &amp; Expertise
        </p>

        {/* ========== 11. TRUST LINKS ========== */}
        <div className="cs-trust-links">
          <Link to="/hipaa">HIPAA Compliance</Link>
          <span className="cs-trust-divider">•</span>
          <Link to="/terms">Terms of Service</Link>
          <span className="cs-trust-divider">•</span>
          <Link to="/privacy">Privacy Policy</Link>
        </div>

        {/* ========== 12. EARLY-ACCESS GATE — moved up to the hero
            on 2026-02-08 per founder spec ("can't see the access
            code link, move it higher, below the Coming Soon
            label"). ========== */}
      </div>
    </div>
  );
};

export const hasSiteAccess = () => {
  return localStorage.getItem(STORAGE_KEY) === 'granted';
};

export const revokeSiteAccess = () => {
  localStorage.removeItem(STORAGE_KEY);
};

// Admin-only function to grant access (used by admin portal)
export const grantSiteAccess = () => {
  localStorage.setItem(STORAGE_KEY, 'granted');
};

export default ComingSoon;
